import { ChangeDetectionStrategy, Component, input, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SmsAttemptDto, SmsAttemptStatus, SmsGatewayWebhookEvent, SmsTriggers } from '@workspace/data-types';
import { CodeBlockComponent, LuxonDatePipe } from '@workspace/ng-aura';
import { SmsAttemptStatusDisplayPipe, SmsTriggerDisplayPipe } from '@workspace/ng-data-access';

@Component({
  selector: 'core-sms-attempts',
  templateUrl: './sms-attempts.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatTooltipModule,
    CodeBlockComponent,
    LuxonDatePipe,
    SmsAttemptStatusDisplayPipe,
    SmsTriggerDisplayPipe,
  ],
})
export class SmsAttemptsCoreComponent {
  attempts = input.required<SmsAttemptDto[]>();

  readonly smsAttemptStatus = SmsAttemptStatus;
  readonly smsTriggers = SmsTriggers;

  getGatewayEvents(attempt: SmsAttemptDto): unknown[] {
    const gr = attempt?.metadata?.['gatewayResponse'];
    return Array.isArray(gr) ? gr : [];
  }

  getEventTitle(event: unknown): string {
    const ev = event as Partial<SmsGatewayWebhookEvent> & { state?: string };
    if (ev?.event === 'sms:sent') return 'Envoyé par le terminal';
    if (ev?.event === 'sms:delivered') return 'Délivré au destinataire';
    if (ev?.event === 'sms:failed') return 'Échec de transmission';
    if (ev?.event === 'sms:cancelled') return 'Envoi annulé';
    if (ev?.state) return 'Enregistré sur la passerelle';
    return 'Événement passerelle';
  }

  getEventIcon(event: unknown): string {
    const ev = event as Partial<SmsGatewayWebhookEvent> & { state?: string };
    if (ev?.event === 'sms:sent') return 'send';
    if (ev?.event === 'sms:delivered') return 'done_all';
    if (ev?.event === 'sms:failed') return 'error';
    if (ev?.event === 'sms:cancelled') return 'cancel';
    return 'cell_tower';
  }

  getEventColorClass(event: unknown): { bg: string; text: string; dot: string } {
    const ev = event as Partial<SmsGatewayWebhookEvent> & { state?: string };
    if (ev?.event === 'sms:delivered') {
      return { bg: 'bg-jade-3', text: 'text-jade-11', dot: 'bg-jade-9' };
    }
    if (ev?.event === 'sms:sent') {
      return { bg: 'bg-green-3', text: 'text-green-11', dot: 'bg-green-9' };
    }
    if (ev?.event === 'sms:failed') {
      return { bg: 'bg-red-3', text: 'text-red-11', dot: 'bg-red-9' };
    }
    if (ev?.event === 'sms:cancelled') {
      return { bg: 'bg-neutral-3', text: 'text-neutral-11', dot: 'bg-neutral-9' };
    }
    return { bg: 'bg-amber-3', text: 'text-amber-11', dot: 'bg-amber-9' };
  }

  getEventDate(event: unknown): string | Date | null {
    const ev = event as Partial<SmsGatewayWebhookEvent> & {
      createdAt?: string;
      states?: Record<string, string>;
    };
    return (
      ev?.payload?.deliveredAt ||
      ev?.payload?.sentAt ||
      ev?.payload?.failedAt ||
      ev?.createdAt ||
      (ev?.states?.['Pending'] ?? null)
    );
  }

  getEventRecipient(event: unknown): string | null {
    const ev = event as Partial<SmsGatewayWebhookEvent> & {
      recipients?: Array<{ phoneNumber?: string }>;
    };
    return ev?.payload?.recipient || ev?.payload?.phoneNumber || ev?.recipients?.[0]?.phoneNumber || null;
  }

  getEventParts(event: unknown): number | null {
    const ev = event as Partial<SmsGatewayWebhookEvent>;
    return ev?.payload?.partsCount ?? null;
  }

  getEventReason(event: unknown): string | null {
    const ev = event as Partial<SmsGatewayWebhookEvent>;
    return ev?.payload?.reason || null;
  }
}
